<template>
  <div class="catalog-product-modal">
    <div class="catalog-product-main-wrapper">
      <div
        class="error-text"
        v-if="
          hasInvalidPage && Array.isArray(getRows) && getRows.length && images
        "
      >
        <img src="@/assets/images/icons/alert-circle.svg" />
        Tablodaki eksik veya yanlış girdiğiniz bilgileri düzenleyin. Varsa,
        tablonun diğer sayfalarını da kontrol edin.
      </div>

      <ProductDetailTableBulkOperations
        v-show="getRowsCount"
        :selectedCurrency="getSelectedCurrency"
        pid="bulk"
        :categoryId="category"
        @applySelected="applySelected"
        ref="bulkOperationsRef"
        :selectedRowsLength="getSelectedRowsLength"
      />

      <SingleProductTable
        v-show="getRowsCount"
        ref="productDetailTableRef"
        :variants="tableRows"
        :attributes="props.attributes"
        :columns="columnList"
        :selectedCurrency="getSelectedCurrency"
        :pinnedColumns="pinnedColumns"
      />
      <div class="button-row" :class="{ 'single-button': !shouldShowAddVariantButton }">
        <AppButton
          v-if="shouldShowAddVariantButton"
          secondary
          md
          @click="emit('openAddVariantModal')"
          :buttonStyle="{ width: '148px' }"
          title="Varyant Ekle"
        >
          <template #prependIcon>
            <Icons name="Plus" color="#5d3ebc" size="sm" />
          </template>
        </AppButton>
        <AppButton
          v-if="shouldShowSaveButton"
          primary
          md
          @click="handleSaleVariantSave"
          :buttonStyle="{ width: showSuccessState ? 'auto' : '183px' }"
          :disabled="!isTableReadyToSave || isLocalSaving"
          :loading="isLocalSaving"
          :title="
            showSuccessState
              ? 'Değişiklikler Kaydedildi'
              : 'Değişiklikleri Kaydet'
          "
        >
          <template #prependIcon>
            <span v-if="isLocalSaving" class="local-spinner" />
            <Icons
              v-else-if="showSuccessState"
              name="Checked"
              color="#B4B4B4"
              size="md"
              class="success-icon"
            />
          </template>
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from 'vuex';
import useColumnService from "../../services/getProductVariants";
import { Icons, AppButton } from "@n11/so-ui-components";
import SingleProductTable from "@/components/Table/SingleProductTable";
import ProductDetailTableBulkOperations from "../ProductDetailTableBulkOperations.vue"
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";
import Request from "@/services/request";
import { SUBMIT_SALE_VARIANT_INFO } from "@/utils/enum/endpoints";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const props = defineProps({
  attributes: { type: Array, default: () => [] },
  category: { type: [Number, null], required: true },
  selectedCurrency: { type: Object, required: true, default: () => ({}) },
  variantRefs: { type: String, default: "" },
  tableData: { type: Array, default: () => [] },
  hasVariantAttribute: { type: Boolean, default: false },
});
const emit = defineEmits(["openAddVariantModal"]);

const store = useStore();

const shouldShowAddVariantButton = computed(() => {
  if (props.hasVariantAttribute) {
    return true;
  }
  
  return false;
});

const shouldShowSaveButton = computed(() => {
  if (props.hasVariantAttribute) {
    return true;
  }
  
  if (props.variantRefs === 'productDetailProductVariants') {
    return false;
  }
  
  return true;
});
const request = new Request();
const tableRows = ref([]);
const productDetailTableRef = ref();
const columnList = ref([]);
const validateRef = ref(null);
const bulkOperationsRef = ref();
const dirtyRows = ref();
const selectedRowsAfterApply = ref([]);
const isLocalSaving = ref(false);
const showSuccessState = ref(false);

const pinnedColumns = {
  left: ['select-col', 'variantTitle'],
  right: ['actions']
}

watch(
  () => props.tableData,
  (newData) => {
    const rows = Array.isArray(newData) ? [...newData] : [];
    rows.forEach(row => {
      if (row.vatRate === undefined || row.vatRate === null || row.vatRate === "") {
          validateRef.value?.(row.vatRate, 'vatRate', row, true, rows); 
      }
    });
    
    tableRows.value = rows;
  },
  { immediate: true },
);

const resetDirtyState = () => {
  const tableData = productDetailTableRef.value?.table?.options?.meta.getData();
  if (!tableData || tableData.length === 0) return;
  
  tableData.forEach(row => {
    Object.keys(row.validations).forEach(key => {
      row.validations[key].isDirty = false;
    });
  });
  
  tableRows.value = [...tableRows.value];
  selectedRowsAfterApply.value = [];
};
const hasAnyError = computed(() => {
  const rows = getRows.value;
  
  if (!Array.isArray(rows) || rows.length === 0) return false;

  return rows.some(row => {
    if (!row.validations) return false;
    
    return Object.values(row.validations).some(
      (v) => v && v.error === true
    );
  });
});

const isTableReadyToSave = computed(() => {
  const rows = getRows.value;
  
  if (!Array.isArray(rows) || rows.length === 0) return false;
  if (hasAnyError.value) return false;

  const updatedRows = rows.filter(row => {
    if (!row.validations) return false; 

    return Object.values(row.validations).some(
      (v) => v && v.isDirty === true && v.error === false
    );
  });

  dirtyRows.value = updatedRows;
  return updatedRows.length > 0;
});

const getCategoryId = computed(() => props.category);
const getSelectedCurrency = computed(() => props.selectedCurrency);
const getRowsCount = computed(() => tableRows.value?.length);
const images = computed(() => store.getters["common/getHasImageError"]);
const hasInvalidPage = computed(() =>
  productDetailTableRef.value?.getInvalidPageNumbers()?.length === 0
    ? false
    : true
);

const getSelectedRowsLength = computed(
  () => Object.keys(productDetailTableRef.value?.getSelectedRows() ?? []).length
);

const getRows = computed(() =>
  productDetailTableRef.value?.table?.options?.meta.getData()
);

const getTableRawData = () => {
  if (productDetailTableRef.value) {
    return productDetailTableRef.value.getRawData();
  }
  
  return [];
};

const formatTableRowsForPayload = (tableRows) => {
  if (!tableRows || tableRows.length === 0) {
    return { products: [] };
  }

  const productsPayload = tableRows.map(row => {
    const salePrice = row.salePrice?._value ?? row.salePrice;
    const listPrice = row.listPrice?._value ?? row.listPrice;
    const vatRate = row.vatRate?._value ?? row.vatRate;
    const stock = row.stock?._value ?? row.stock;
    const duration = row.duration?._value ?? row.duration;
    const stockCode = row.stockCode?._value ?? row.stockCode;
    const productId = row.id?._value ?? row.id;
    const preparingTime = duration;
    const sellerStockCode = stockCode;

    return {
      sellerStockCode: sellerStockCode ? String(sellerStockCode) : undefined,
      productId: productId ? Number(productId) : undefined,
      salePrice: salePrice ? Number(salePrice) : undefined,
      listPrice: listPrice ? Number(listPrice) : undefined,
      vatRate: vatRate ? Number(vatRate) : undefined,
      stock: stock ? Number(stock) : undefined,
      preparingTime: preparingTime ? Number(preparingTime) : 1,
      currencyType: getSelectedCurrency?.value?.currencyCode === 'TRY' ? 'TL' : getSelectedCurrency?.value?.currencyCode
    };
  }).filter(product => product.productId && product.sellerStockCode);

  return {
    products: productsPayload
  };
};

async function handleSaleVariantSave() {
  try {
    if (isLocalSaving.value) return;
    isLocalSaving.value = true;
    if (showSuccessState.value) showSuccessState.value = false;

    const tabName = props.variantRefs === 'productDetailProductVariants' 
      ? 'Ürünün Diğer Varyantları' 
      : 'Aynı Model Koduyla Gruplanan Ürünler';

    const payload = formatTableRowsForPayload(dirtyRows.value);

    const dirtyIds = selectedRowsAfterApply.value.length > 0
      ? [...selectedRowsAfterApply.value]
      : payload.products.map(p => p.productId).filter(Boolean);

    const { data } = await request.patch(SUBMIT_SALE_VARIANT_INFO, payload, {
      baseURL: '/sellerofficeapi/rest',
      headers: { Accept: 'application/json' },
    });

    resetDirtyState()

    const allErrorMessages = [];
    let hasFailure = false;
    (Array.isArray(data) ? data : []).forEach(item => {
      if (!item.success) {
        hasFailure = true;
        if (item.errors && item.errors.length > 0) {
          item.errors.forEach(error => {
            allErrorMessages.push(`Stok Kodu: ${item.sellerStockCode} - ${error.errorMessage}`);
          });
        }
      }
    });

    if (hasFailure) {
      const combinedErrorMessage = allErrorMessages.join('\n');
      store.commit("common/SET_TOASTER_DATA", {
        type: ToastMessageTypes.ERROR,
        title: '',
        description: combinedErrorMessage,
      });
    } else {
      store.commit(
        "common/SET_TOASTER_DATA",
        {
          type: ToastMessageTypes.SUCCESS,
          title: "Başarılı",
          description: "Yaptığınız değişiklikler kaydedilmiştir.",
        },
        { root: true }
      );

      sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
        button_name: 'Değişiklikleri Kaydet',
        button_type: 'Save Changes',
        button_area: 'Other Product Options',
        item_id: dirtyIds.length > 0 ? dirtyIds : undefined,
        tab: tabName,
      });

      showSuccessState.value = true;
    }
  } catch (err) {
    console.log("err", err);
  } finally {
    isLocalSaving.value = false;
  }
}

watch(isTableReadyToSave, (isDirtyNow) => {
  if (isDirtyNow && showSuccessState.value) {
    showSuccessState.value = false;
  }
});

watch(
  () => props.variantRefs,
  () => {
    isLocalSaving.value = false;
    showSuccessState.value = false;
  },
);

const selectedRowIds = computed(
  () =>
    productDetailTableRef.value?.getSelectedRowIds?.() ??
    Object.keys(productDetailTableRef.value?.getSelectedRows() ?? [])
);

const bulkForceValidate = async () => {
  const allRows = productDetailTableRef.value?.table?.options?.meta.getData();
  
  if (!allRows || allRows.length === 0) return;

  const fieldsToValidate = ['listPrice', 'salePrice', 'duration', 'stockCode', 'stock', 'vatRate'];

  const validationPromises = [];

  allRows.forEach((row) => {
    fieldsToValidate.forEach((field) => {
      const promise = validateRef.value(row[field], field, row, true, allRows);
      validationPromises.push(promise);
    });
  });

  await Promise.all(validationPromises);
  
  tableRows.value = [...allRows];
};


const applySelected = async () => {
  if (!productDetailTableRef.value) return;

  const ids = selectedRowIds.value;
  if (!ids.length) return;
  selectedRowsAfterApply.value = [...ids];

  const ops = bulkOperationsRef.value?.bulkOperationsData;
  const updates = {};
  ["gtin", "listPrice", "salePrice", "stock", "duration", "vatRate"].forEach(
    (f) => {
      const v = ops?.[f];
      if (v !== undefined && v !== null && v !== "" && v !== 0) updates[f] = v;
    }
  );
  if (!Object.keys(updates).length) return;

  productDetailTableRef.value.applyBulkToIds?.(ids, updates);

  tableRows.value = tableRows.value.map((r) =>
    ids.includes(r.id) ? { ...r, ...updates } : r
  );
  await bulkForceValidate();
  productDetailTableRef.value.table.resetRowSelection();
  const tabName = props.variantRefs === 'productDetailProductVariants' 
    ? 'Ürünün Diğer Varyantları' 
    : 'Aynı Model Koduyla Gruplanan Ürünler';
  sendProductDetailEvent(GA4_EVENT.APPLY_SELECTED_ONES, {
    button_name: 'Seçilenlere Uygula',
    button_type: 'Apply Selected Ones',
    button_area: 'Other Product Options',
    item_id: ids,
    tab: tabName,
  });
};

watch(getCategoryId, (id) => {
  const { columns, validate } = useColumnService(id, props.selectedCurrency);
  validateRef.value = validate;
  columnList.value = columns;
}, { immediate: true });

watch(getSelectedCurrency, (currency) => {
  const { columns, validate } = useColumnService(props.category, currency);
  validateRef.value = validate;
  columnList.value = columns;
});

defineExpose({
  getVariantsToSave: getTableRawData,
  validateRef,
  resetDirtyState
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
.catalog-product-main-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;

  .error-text {
    padding: 16px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    font-size: 13px;
    line-height: 18px;
    color: $colorN80;
    background: $colorE05;
    border-radius: 6px;
    align-items: center;
    letter-spacing: -0.05px;

    img {
      width: 22px;
      height: 22px;
    }
  }

  .button-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 16px 0 24px 0;
    
    &.single-button {
      justify-content: flex-end;
    }
  }
}

.local-spinner {
  display: inline-block;
  flex: 0 0 16px;
  min-width: 16px;
  min-height: 16px;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.success-icon {
  margin-right: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
